import { createFileRoute } from '@tanstack/react-router'

import { UserRole } from '@/api/admin-types'
import { useCreateInvite } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/users/invite')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.Users.InviteTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<InviteUserForm />
		</RouteModal>
	)
}

function InviteUserForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()

	const createInvite = useCreateInvite()

	const form = useAppForm({
		defaultValues: {
			email: '',
			role: UserRole.User
		},
		onSubmit: async ({ value }) => {
			const res = await createInvite({
				body: {
					email: value.email,
					role: value.role
				}
			})
			if (!res.isSuccess) {
				if (res.type === 'USER_ALREADY_EXISTS') {
					toast.error(L.Settings.Users.Invite.UserAlreadyExists({ email: value.email }))
					return
				}
				toast.error(L.Error.SomethingWentWrong())
				return
			}
			toast.success(L.Settings.Users.InviteSentTo({ email: value.email }))
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Users.InviteTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='email'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Email()}
									placeholder={L.Settings.Users.Invite.EmailPlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='role'
							children={field => (
								<field.SelectField
									label={L.Fields.Role()}
									options={[
										{ value: UserRole.User, label: L.Enum.UserRole('USER') },
										{ value: UserRole.Admin, label: L.Enum.UserRole('ADMIN') },
										{ value: UserRole.Developer, label: L.Enum.UserRole('DEVELOPER') }
									]}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton label={L.Settings.Users.SendInvite()} />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
